import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderComponent } from "../../../modules/components/loader/loader.component";
import { environment } from '../environments/environment';
import { CommonService } from '../service/common.service';
import { defaultDashboardPath } from '../service/default-dashboard';
import { CredentialVaultService } from '../service/credential-vault.service';
import { LoginService } from '../service/login.service';
import { AppState } from '../service/state-api-handler.service';
import { TawkService } from '../service/tawk.service';
import { UserService } from '../service/user.service';
import { applyUserProfile } from '../service/apply-user-profile';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { clearAllState } from '../state-files/action';
import { timeout, retry, catchError, EMPTY, timer } from 'rxjs';
import { clearCustomizationFlags, writeCustomizationFlags } from '../shared/customization-flags';

@Component({
  selector: 'app-auth-redirect',
  standalone: true,
  imports: [CommonModule, LoaderComponent, TranslateModule],
  templateUrl: './auth-redirect.component.html',
})
export class AuthRedirectComponent {
  email: string = '';
  user_date: string = '';
  private userid = '';
  errorMessage: string = '';
  LoginDetails: any[] = [];
  AssessorRole: any[] = [];
  PerformerReviewerRole: any[] = [];
  isLoading: boolean = true;
  skipOtp: boolean = false;

  /**
   * Injects the collaborators used by the tokenised redirect login flow.
   *
   * @param loginService - Exchanges the redirect token for a session.
   * @param userService - Session identity accessor and token/HMAC helper.
   * @param commonService - Shared helpers used across the auth entry points.
   * @param router - Router used for the post-login landing navigation.
   * @param http - HTTP client for requests made outside the typed services.
   * @param routes - Active route supplying the encoded redirect parameters.
   * @param tawkService - Syncs the signed-in visitor into the support chat widget.
   * @param store - NgRx store seeded with the signed-in user's state.
   * @param credentialVault - Holds credentials across the redirect handshake.
   * @param translate - Supplies translated messages shown during redirect.
   */
  constructor(

    private loginService: LoginService,
    private userService: UserService,
    private commonService: CommonService,
    private router: Router,
    private http: HttpClient,
    private routes: ActivatedRoute,
    private tawkService: TawkService,
    private store: Store<AppState>,
    private credentialVault: CredentialVaultService,
    private translate: TranslateService
  ) { }

  /**
  * Decodes an encoded redirect URL, extracts the page name and query parameters, and stores them in sessionStorage.
  * @example
  * handleRedirectUrl(encodedUrl)
  * undefined
  * @param {string} encodedUrl - URL-encoded redirect URL containing a page path and optional query string.
  * @returns {void} Does not return a value.
  **/
  handleRedirectUrl(encodedUrl: string) {
    const decodedUrl = decodeURIComponent(encodedUrl);

    // Example: "~/Controls/frmUpcomingCompliancessNew.aspx?type=Statutory&filter=10&role=Performer..."
    const [pagePart, paramString] = decodedUrl.split('?');

    const pageName = pagePart.replace("~", ""); // remove ~

    const paramObj: any = {};

    if (paramString) {
      const searchParams = new URLSearchParams(paramString);

      searchParams.forEach((value, key) => {
        paramObj[key] = value;
      });
    }

    // Store in sessionStorage
    sessionStorage.setItem("pageName", pageName);
    sessionStorage.setItem("paramObj", JSON.stringify(paramObj));

  }


  /**
   * Initializes the authentication redirect flow: clears secondary user data,
   * reads query parameters, and validates the redirect token. On success it
   * persists the redirect credential to the encrypted vault, stores the
   * verifier from the same response, and loads the user details. On any failure (missing user_email/user_date, non-200, or a
   * transport error) it calls failRedirectAuth() to roll back the optimistic
   * auth markers — clearing the vault and the 'userToken' flag — so a failed
   * redirect can never leave the app half-authenticated.
   * @example
   * gOnInit()
   * void
   * @param {void} - This function does not accept any parameters.
   * @returns {void} Returns nothing.
   **/
  ngOnInit(): void {
    sessionStorage.removeItem('secondary_user_id');
    sessionStorage.removeItem('secondary_user_name');
    sessionStorage.removeItem('secondary_user_email');
    
    this.clearComponentFilterStates();
    this.clearAllReduxData();

    this.routes.queryParams.subscribe((params) => {
      const { token, redirectUrl, StandardReport_SkipOtp } = params;
      this.userid = token;
      this.skipOtp = StandardReport_SkipOtp === 'true';
      if (redirectUrl) {
        this.handleRedirectUrl(redirectUrl);
      }

    sessionStorage.setItem('userToken', 'logged-in');
    sessionStorage.setItem('IsProductMappingApplicable', 'true');
    this.loginService.validateUserRedirectionNew(this.userid, 0).subscribe({
      next: (response) => {
        if (response.status === 200) {
          const responseData = response.body;
          // The e-mail plus ONE credential are required for the redirect re-auth
          // contract: a missing/empty credential would poison the vault and every
          // later silent token refresh would fail with a forced session expiry, so
          // stop here with an explicit auth error instead of persisting it.
          const handoffSecret: string = responseData?.handoff_secret || '';
          if (!responseData?.user_email || (!handoffSecret && !responseData?.user_date)) {
            // instant() (not the pipe): this runs in a subscribe callback, and
            // the root TranslateModule has loaded the language file by now.
            this.failRedirectAuth(this.translate.instant('invalidRedirectAuthResponse'));
            console.error('validateUserRedirectionNew returned no usable credentials');
            return;
          }
          // Tear down any session already in this tab (the legacy portal can hand
          // off a different user to a tab that is still logged in): without this,
          // ensureValidToken() would reuse the previous user's still-valid token
          // and verifier for this user's details call. Runs before the new
          // credential is vaulted, because resetUserServiceState() wipes the vault.
          this.userService.resetUserServiceState();
          this.userService.clearUserData();
          this.email = responseData.user_email;
          this.user_date = responseData.user_date || '';
          if (handoffSecret) {
            // Current API: a server-issued hand-off secret (hashed at rest, bound to
            // this user, expiring, revoked on logout). UserService presents it to
            // token-generate as `handoff_secret` on the first mint, every refresh
            // and every company switch. It is not a password and is vaulted apart.
            this.credentialVault.setHandoffSecret(handoffSecret);
          } else {
            // Older API (no handoff_secret yet): user_date is the value the redirect
            // flow re-authenticates with, stored as the password. This branch is what
            // keeps the new SPA working against an API that has not been upgraded —
            // it is not dead code until the phase-3 cleanup in the design doc.
            this.credentialVault.setPassword(this.user_date);
          }
          sessionStorage.setItem('LoginEmail', this.email);
          // The redirect response carries the HMAC verifier, so no separate
          // verify-email round trip is needed. If it is absent, UserService
          // fetches it after the first token mint.
          if (responseData.verifier) {
            this.userService.setVerifier(atob(responseData.verifier));
          }
          sessionStorage.setItem('isVerified', 'true');
          this.getUserDetails();
          this.getPTTokenForCookies();
          sessionStorage.setItem('userToken', 'logged-in');
        } else {
          this.failRedirectAuth(response.body?.message || this.translate.instant('invalidCredentials'));
        }
      },
      error: (error) => {
        this.failRedirectAuth(error.error?.message || this.translate.instant('loginFailedTryAgain'));
        console.error("ngit, Error while login:", error);
        if (environment.production === true) {
          alert(this.translate.instant('loginFailedTryAgain') + "(GURD)");

          window.location.href = `${environment.OLD_NEW_URL}/ProductMapping/MappingStructure`;
          return;
        }
      }
    });
    });
  }

  /**
   * Rolls back the optimistic auth markers this redirect flow sets up front
   * (the 'userToken' logged-in flag, the 'isVerified' guard flag, and any
   * vaulted credential) so a failed redirect login can never leave the app
   * half-authenticated, then surfaces the error and stops the loader.
   * @param message User-facing error text to display.
   */
  private failRedirectAuth(message: string): void {
    this.credentialVault.clear();
    sessionStorage.removeItem('userToken');
    // Also clear the verification flag: weeklyReportAuthGuard treats
    // isVerified === 'true' as authenticated, so a stale value from a prior
    // session would still authorize guarded routes after this failure.
    sessionStorage.removeItem('isVerified');
    this.errorMessage = message;
    this.isLoading = false;
  }

  /**
  * Fetches PT tokens from the login service and stores available tokens in session storage.
  * @example
  * etPTTokenForCookies()
  * undefined
  * @returns {void} Does not return a value.
  **/
  getPTTokenForCookies() {
    this.loginService.getPTTokenForCookies().subscribe({
      next: (response) => {
        if (response.status === 200 && response.body && response.body.result) {
          const { result } = response.body;
          const helpAIToken = result.find((item: any) => item.help_aitoken)?.help_aitoken || '';
          const regAIToken = result.find((item: any) => item.reg_aitoken)?.reg_aitoken || '';
          const sqlAIToken = result.find((item: any) => item.sqlaitoken)?.sqlaitoken || '';

          if (helpAIToken) {
            sessionStorage.setItem('helpAIToken', helpAIToken);
          }
          if (regAIToken) {
            sessionStorage.setItem('regAIToken', regAIToken);
          }
          if (sqlAIToken) {
            sessionStorage.setItem('sqlAIToken', sqlAIToken);
          }
        }
      },
      error: (error) => {
        console.error("Error fetching PT token:", error);
      }
    });
  }
  /**
   * Handles a successful user details response by storing user data and initializing related UI state.
   * @example
   * handleUserDetailsResponse(response)
   * undefined
   * @param {any} response - The HTTP response containing the user details payload.
   * @returns {void} Returns nothing.
  **/
  handleUserDetailsResponse(response: any): void {
    if (response.status !== 200 || !response.body) {
      console.error('Unexpected response:', response);
      return;
    }

    const user = response.body;

    this.setUserServiceData(user);
    this.setSessionStorageData(user);
    this.setDefaultDateFilters();

    this.getCustomisationList();
  }
  /**
  * Sets user-related data in application services after authentication redirect.
  * @example
  * etUserServiceData(user)
  * void
  * @param {any} user - User object containing role, identifiers, flags, and other profile data.
  * @returns {void} No return value.
  **/
  setUserServiceData(user: any): void {
    applyUserProfile(this.userService, user);

    this.tawkService.setVisitor(user.name, this.email, user.tawk_hash);
  }
  /**
  * Stores user-related flags and defaults in session storage for downstream application use.
  * @example
  * etSessionStorageData(user)
  * undefined
  * @param {any} user - User object containing visibility and feature-flag properties.
  * @returns {void} No return value.
  **/
  setSessionStorageData(user: any): void {
    sessionStorage.setItem('is_certificate_visible', JSON.stringify(user.is_certificate_visible));
    sessionStorage.setItem('is_notification_dot', user.notification_dot_visible);
    sessionStorage.setItem('is_messagecenter_dot', user.message_center_dot_visible);

    setTimeout(() => {
      sessionStorage.setItem('is_certificate_entity', JSON.stringify(user.is_certificate_entity));
    }, 0);

    if (user.hasOwnProperty('is_penalty_not_display_customer')) {
      sessionStorage.setItem(
        'isPenaltyCardVisible',
        user.is_penalty_not_display_customer ? 'true' : 'false'
      );
    }

    if (user.isProductMappingApplicable !== undefined) {
      sessionStorage.setItem(
        'IsProductMappingApplicable',
        user.isProductMappingApplicable ? 'true' : 'false'
      );
    }

  }
  /**
  * Sets default date filters in session storage to the first day of the current month and today's date if they are not already present.
  * @example
  * setDefaultDateFilters()
  * void
  * @returns {void} No return value.
  **/
  setDefaultDateFilters(): void {
    const currentDate = new Date();

    if (!sessionStorage.getItem('from_date')) {
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      sessionStorage.setItem('from_date', firstDay.toISOString().split('T')[0]);
    }

    if (!sessionStorage.getItem('to_date')) {
      sessionStorage.setItem('to_date', currentDate.toISOString().split('T')[0]);
    }
  }
  
  handleLoginError(error: any): void {
    console.error('Error while login:', error);
    console.error('Error message:', error.error?.message || 'Unknown error');

    if (environment.production === true) {
      alert(this.translate.instant('loginFailedTryAgain') + "(NG)");
      window.location.href = `${environment.OLD_NEW_URL}/ProductMapping/MappingStructure`;
    }
  }

  /**
   * Fetches user details using the current email and handles the login response.
   * @example
   * etUserDetails()
   * void
   * @returns {void} No return value.
   **/
getUserDetails(): void {
  const params = {
    user_email: this.email,
    customer_id: -1,
    common_user_id: -1
  };

  this.loginService.getUserDetails(params).pipe(
    timeout(15000),                          // abort if server hangs beyond 15s
    retry({ count: 2, delay: (err) => { if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500) throw err; return timer(1500); } }),
    catchError(error => {
      this.handleLoginError(error);
      return EMPTY;
    })
  ).subscribe(response => this.handleUserDetailsResponse(response));
}

  private filterToStatusIdMap: { [key: string]: string } = {
    'U': '-1',
    '-1': '-1',
    'Upcoming': '0',
    'Overdue': '1',
    'PendingForReview': '2',
    'PR': '2',
    'Pending For Review': '2',
    'Rejected': '3',
    'R': '3',
    'Completed': '5',
    'C': '5',
    'DeviationApplied': '21',
    'DA': '21',
    'DeviationRejected': '22',
    'DR': '22',
    'DeviationApproved': '23',
    'DP': '23'
  };

  /**
   * Maps a legacy portal filter name to the Regtrack 2.0 status id.
   *
   * Both a missing parameter and an unrecognised one resolve to `'-1'` (no
   * status filter), so an unknown legacy link lands on an unfiltered page
   * rather than failing; the unrecognised case is logged to surface drift
   * between the two portals' filter names.
   * @param filterParam - The legacy filter name from the redirect URL.
   * @returns The matching status id, or `'-1'` when absent or unrecognised.
   */
  private convertFilterToStatusId(filterParam: string): string {
    if (!filterParam) {
      return '-1';
    }

    const statusId = this.filterToStatusIdMap[filterParam];
    
    if (statusId !== undefined) {
      return statusId;
    }

    console.warn(`Unknown filter parameter: ${filterParam}. Defaulting to '-1'`);
    return '-1';
  }

  /**
   * Maps a status id to the short status code the report pages expect.
   *
   * An unmapped id yields an empty string, which the destination reads as "no
   * status filter" — the same result as the explicit `'-1'` entry.
   * @param statusId - The status id, typically from {@link convertFilterToStatusId}.
   * @returns The short status code, or an empty string when the id is unmapped.
   */
   private getStaParaValue(statusId: string): string {
    const staParaMap: { [key: string]: string } = {
      '-1': '',
      '0': 'U',
      '1': 'O',
      '2': 'PR',
      '3': 'R',
      '5': 'C',
      '21': 'DA',
      '22': 'DR',
      '23': 'DP'
    };

    return staParaMap[statusId] || '';
  }

  private riskStatusMap: { [key: string]: string } = {
    '-1': 'U',
    '0': 'U',
    '1': 'O',
    '2': 'PR',
    '3': 'R',
    '5': 'C',
    '21': 'U',
    '22': 'U',
    '23': 'U'
  };

  /**
   * Handles the API response containing user role details.
   * Sets distinct roles and attempts to redirect based on stored page parameters.
   * Falls back to default dashboard if no redirect match is found.
   * @param response - The API response containing role details in body.result
   */
  handleRoleResponse(response: any): void {
    if (!response?.body?.result) return;

    const apiResponse = response.body.result;
    this.userService.setDistinctRoles(apiResponse);

    const pageName = sessionStorage.getItem('pageName') || '';
    const paramObj = this.getParamObj();

    if (this.handleRedirect(pageName, paramObj)) {
      return;
    }

    this.navigateToDefaultDashboard();
  }

  /**
   * Maps ASPX page names to their corresponding Angular routes with appropriate state/parameters.
   * Handles legacy redirects from the old ASP.NET application to the new Angular routes.
   * @param pageName - The ASPX page name from the redirect URL (e.g., 'frmUpcomingCompliancessNew.aspx')
   * @param paramObj - Object containing query string parameters from the original ASPX URL
   * @returns true if a matching redirect was found and executed, false otherwise
   */
  handleRedirect(pageName: string, paramObj: any): boolean {

    const redirectMap = [
      // {
      //   match: 'frmUpcomingCompliancessNew.aspx',
      //   action: () => {
      //     const riskStatus = this.riskStatusMap[paramObj.filter] || 'U';
      //     const newLocal = paramObj.type || 'Statutory';
      //     const complianceType = newLocal;

      //     sessionStorage.removeItem('pageName');
      //     sessionStorage.removeItem('paramObj');

      //     this.router.navigate(['/workspaceCompliance'], {
      //       state: {
      //         sta_para: riskStatus,
      //         selectedRiskStatus: paramObj.filter,
      //         selectedComplianceType: complianceType
      //       }
      //     });
      //   }
      // },
      {
        match: 'frmUpcomingCompliancessNew.aspx',
        action: () => {
          // CHANGED: Convert filter parameter to numeric status ID
          const filterParam = paramObj.filter || '-1';
          const statusId = this.convertFilterToStatusId(filterParam);
          
          const complianceType = paramObj.type || 'Statutory';

          sessionStorage.removeItem('pageName');
          sessionStorage.removeItem('paramObj');

          this.router.navigate(['/workspaceCompliance'], {
            state: {
              sta_para: this.getStaParaValue(statusId),
              selectedRiskStatus: statusId,  //Now sends numeric ID, not text
              selectedComplianceType: complianceType
            }
          });
        }
      },
      {
        match: 'checkliststatusperformer.aspx',
        action: () => {
          this.setLocal('performerEditChecklistData_scheduleonid', paramObj.sId);
          this.navigateAndClear('/editChecklist');
        }
      },
      {
        match: 'Checkliststatusreviewer.aspx',
        action: () => {
          this.setLocal('performerEditChecklistData_scheduleonid', paramObj.sId);
          this.navigateAndClear('/editChecklist');
        }
      },
      {
        match: 'internalCheckliststatusperformer.aspx',
        action: () => {
          this.setLocal('internalPerformerEditChecklistData_scheduleonid', paramObj.sId);
          this.navigateAndClear('/editInternalChecklist');
        }
      },
      {
        match: 'PerCompliancestatusTransaction.aspx',
        action: () => {
          this.setLocal('statuoryLicensePerformerData_scheduleonid', paramObj.SOID);
          this.navigateAndClear('/statutoryLicensePerformerEdit');
        }
      },
      {
        match: 'RevComplianceStatusTransaction.aspx',
        action: () => {
          this.setLocal('statuoryLicenseReviewerData_scheduleonid', paramObj.SOID);
          this.navigateAndClear('/statutoryLicenseReviewerEdit');
        }
      },
       {
        match: 'PerInternalCompliancestatusTransaction.aspx',
        action: () => {
          this.setLocal('internalLicensePerformerData_scheduleonid', paramObj.SOID);
          this.navigateAndClear('/internalLicensePerformerEdit');
        }
      },
      {
        match: 'RevInternalComplianceStatusTransaction.aspx',
        action: () => {
          this.setLocal('internalLicenseReviewerData_scheduleonid', paramObj.SOID);
          this.navigateAndClear('/internalLicenseReviewerEdit');
        }
      },
      {
        match: 'compliancestatusreviewer.aspx',
        action: () => {
          this.setLocal('statutoryReviewerData', paramObj.sId);
          this.navigateAndClear('/statutoryReviewerEdit');
        }
      },
      {
        match: 'InternalComplianceStatusReviewer.aspx',
        action: () => {
          this.setLocal('internalReviewerScheduleOnId', paramObj.sId);
          this.navigateAndClear('/internalReviewerEdit');
        }
      },
      {
        match: 'compliancestatusperformer.aspx',
        action: () => {
          this.setLocal('statutoryPerformerData', paramObj.sId);
          this.navigateAndClear('/statutoryPerformerEdit');
        }
      },
      {
        match: 'InternalComplianceStatusperformer.aspx',
        action: () => {
          this.setLocal('internalPerformerScheduleOnId', paramObj.sId);
          this.navigateAndClear('/internalPerformerEdit');
        }
      },
      {
        match: 'TaskStatusTransactionPerformer.aspx',
        action: () => {
          this.setLocal('statuoryTaskPerformerData_scheduleonid', paramObj.TSOID);
          this.setLocal('task_instance_id', paramObj.TID);
          this.setLocal('compliance_type', paramObj.TCT);
          this.navigateAndClear('/performerTaskEdit');
        }
      },
      {
        match: 'TaskStatusTransactionReviewer.aspx',
        action: () => {
          this.setLocal('statuoryTaskReviewerData_scheduleonid', paramObj.TSOID);
          this.setLocal('reviewer_task_instance_id', paramObj.TID);
          this.setLocal('reviewer_compliance_type', paramObj.TCT);
          this.navigateAndClear('/reviewerTaskEdit');
        }
      }
    ];

    const matched = redirectMap.find(r => pageName.includes(r.match));

    if (matched) {
      matched.action();
      return true;
    }

    return false;
  }

  /**
   * Retrieves and parses the parameter object stored in sessionStorage.
   * @returns Parsed parameter object from sessionStorage, or empty object if not found
   */
  getParamObj(): any {
    const raw = sessionStorage.getItem('paramObj');
    return raw ? JSON.parse(raw) : {};
  }

  /**
   * Stores a value in localStorage and marks that the user came via a redirect link.
   * @param key - The localStorage key to store the value under
   * @param value - The value to store (primitives stored as-is, objects/arrays will be JSON stringified)
   */
  setLocal(key: string, value: any): void {
    window.localStorage.setItem('cameViaRedirectLink', 'true');
    sessionStorage.setItem('redirectSessionActive', 'true');
    const serializedValue = typeof value === 'object' && value !== null
      ? JSON.stringify(value)
      : String(value);
    window.localStorage.setItem(key, serializedValue);
  }

  /**
   * Navigates to the specified route and clears redirect-related session storage.
   * @param route - The Angular route path to navigate to
   */
  navigateAndClear(route: string): void {
    sessionStorage.removeItem('pageName');
    sessionStorage.removeItem('paramObj');
    this.router.navigate([route]);
  }

  /**
   * Navigates to the appropriate default dashboard based on user type.
   * Checks for department head, SPNI user, Trent user, or falls back to standard dashboard.
   */
  navigateToDefaultDashboard(): void {
    this.router.navigate([defaultDashboardPath(this.userService)]);
  }
  /**
   * Handles login/role-related errors by setting an error message, logging the error, and redirecting in production.
   * @example
   * handleRoleError(error)
   * undefined
   * @param {any} error - The error object containing optional server-provided message details.
   * @returns {void} No return value.
   */
  private handleRoleError(error: any): void {
    this.errorMessage =
      error.error?.message || this.translate.instant('loginFailedTryAgain');

    console.error("gurd, Error while login:", error);

    if (environment.production === true) {
      alert(this.translate.instant('loginFailedTryAgain') + "(GUD)");
      // window.location.href =
      window.location.href = `${environment.OLD_NEW_URL}/ProductMapping/MappingStructure`;
    }
  }


  /**
  * Fetches the current user's role details once the customer and user IDs are available.
  * @example
  * etUserRoleDetails()
  * void
  * @returns {void} No return value; the method triggers an asynchronous role lookup and handles the response internally.
  **/
  getUserRoleDetails() {
    const customerId = this.userService.getCustomerId();
    const userId = this.userService.getUserId();

    if (!customerId || !userId) {
      console.warn('User not ready, delaying getUserRoleDetails');
      setTimeout(() => this.getUserRoleDetails(), 200);
      return;
    }

    const params = {
      customer_id: customerId,
      user_id: userId,
      is_sony_mgmt_user: false
    };

    this.commonService.getUserRoleDetails(params).subscribe({
      next: (response) => this.handleRoleResponse(response),
      error: (error) => this.handleRoleError(error)
    });
  }
  /**
   * Handles the customisation API response by storing data, updating role details, and triggering follow-up processing.
   * @example
   * handleCustomisationResponse(response)
   * void
   * @param {any} response - API response object containing customisation results and role information.
   * @returns {void} No return value.
   */
  private handleCustomisationResponse(response: any): void {
    if (!response?.body?.result) {
      console.error('Unexpected response:', response);
      // Drop whatever is stored before giving up. sessionStorage is per tab, so without
      // this a failed customization fetch would leave the PREVIOUS session's flags in
      // place and let them gate the new one's screens. Failing closed (no flags) matches
      // the company switcher, which clears before it refetches.
      clearCustomizationFlags();
      this.getUserRoleDetails();
      return;
    }

    // Written only once the result is known good. The store used to happen above this
    // guard, so a failed response persisted the literal string "undefined", which every
    // reader that parses the value then choked on.
    writeCustomizationFlags(response.body.result);

    this.LoginDetails = response.body.login_details;
    this.AssessorRole = response.body.assessor_role;
    this.PerformerReviewerRole = response.body.performer_reviewer_role;

    this.processCustomisationResult(response.body.result);

    this.getUserRoleDetails();
  }
  /**
  * Processes customization results and updates management and user flags accordingly.
  * @example
  * processCustomisationResult(['MGMT_Total', 'MGMT_Sony'])
  * undefined
  * @param {any} result - Result data expected to be an array of customization flags.
  * @returns {void} No return value.
  **/
  private processCustomisationResult(result: any): void {

    if (!Array.isArray(result)) {
      console.warn('result is not an array');
      this.setMgmtFlags(false, false);
      return;
    }

    const hasMGMTChecklist = result.includes('MGMT_Checklist');

    const hasMGMTTotal = result.includes('MGMT_Total');
    const hasMGMTTotalUnique = result.includes('MGMT_Total_Unique');
    const isMgmt = this.userService.getRole() === 'MGMT';

    const spniUser = result.includes('MGMT_Sony') && isMgmt;
    const trentUser = result.includes('MGMT_Trent') && isMgmt;

    this.userService.setSpniUser(spniUser);
    this.userService.setTrentUser(trentUser);

    this.setMgmtFlags(hasMGMTTotal, hasMGMTTotalUnique);
    this.set3DGraphFlag(result);
    // Default compliance type for filters:
    // - Show 'Statutory' (includes checklist) when the customer has MGMT_Checklist enabled AND is not a Trent user.
    // - Show 'Statutory Excluding Checklist' in all other cases (Trent users always exclude the checklist tab).
    // Only set the default if no value has been stored yet (e.g. on first login); subsequent navigation
    // preserves whatever the user last selected.
    const isStatutoryVisibleForFilters = hasMGMTChecklist && !trentUser; 
    const defaultComplianceTypeForFilters = isStatutoryVisibleForFilters ? 'Statutory' : 'Statutory Excluding Checklist';
    if (!sessionStorage.getItem('selected_compliance_type')) {  
      sessionStorage.setItem('selected_compliance_type', defaultComplianceTypeForFilters);
    }
  }
  private setMgmtFlags(hasTotal: boolean, hasUnique: boolean): void {
    sessionStorage.setItem('is_MGMT_Total', hasTotal ? '1' : '0');
    sessionStorage.setItem('is_MGMT_Total_Unique', hasUnique ? '1' : '0');
  }
  private set3DGraphFlag(result: string[]): void {
    if (!sessionStorage.getItem('Enable3DGraphs')) {
      const enable3D = result.includes('Enable3DGraphs');
      sessionStorage.setItem('Enable3DGraphs', enable3D ? 'true' : 'false');
    }
  }

  /**
   * Fetches the customization list for the current customer and handles the response or fallback on error.
   * @example
   * etCustomisationList()
   * void
   * @returns {void} No return value.
   */
  getCustomisationList(): void {
    const params = {
      customer_id: this.userService.getCustomerId()
    };

    this.loginService.getCustomisationList(params).subscribe({
      next: (response) => this.handleCustomisationResponse(response),
      error: (error) => {
        console.error("Error while fetching customisation list:", error);
        this.getUserRoleDetails();
      }
    });
  }
  
  private clearAllReduxData(): void {
  
      // Prefer a single global clear to avoid missing slices
      this.store.dispatch(clearAllState());
      sessionStorage.removeItem('selected_CustomerBranch_ComplianceDocuments');
    }

  
    /**
   * Clears all component filter states from both Redux and session storage comprehensively.
   * This ensures that when a user navigates to the homepage, all previously selected filters are reset.
   * @example
   * clearComponentFilterStates()
   * undefined
   * @returns {void} No return value.
   */
  private clearComponentFilterStates(): void {
    // Clear detailed report related session storage
    this.clearDetailedReportSessionStorage();
 
    // Clear other component-specific session storage that might contain filter state
    this.clearComplianceDocumentSessionStorage();
    this.clearOverviewSessionStorage();
    this.clearRemarksSessionStorage();
    this.clearReopenComplianceSessionStorage();
 
    // Clear Redux state
    this.clearReduxComponentStates();
  }

  /**
   * Removes all detailed report related session storage keys.
   * @example
   * clearDetailedReportSessionStorage()
   * undefined
   * @returns {void} No return value.
   */
  private clearDetailedReportSessionStorage(): void {
    const detailedReportKeys = [
      'selected_CustomerBranch_detailedReport',
      'detailedReportSelectedCompliances',
      'periodDetailedReport',
      'detailedReportColumnNames',
      'selected_compliance_type',
      'complianceType',
      'previous_filters',
      'detailedReport_gridState',
      'detailedReport_appliedFilters'
    ];
 
    detailedReportKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove session storage key: ${key}`, e);
      }
    });
  }
 
  /**
   * Removes all compliance documents related session storage keys.
   * @example
   * clearComplianceDocumentSessionStorage()
   * undefined
   * @returns {void} No return value.
   */
  private clearComplianceDocumentSessionStorage(): void {
    const complianceDocKeys = [
      'selected_CustomerBranch_ComplianceDocuments',
      'compliance_documents_filters',
      'compliance_docs_period',
      'compliance_docs_compliance_type'
    ];
 
    complianceDocKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove compliance documents key: ${key}`, e);
      }
    });
  }

  /**
   * Removes all overview compliance related session storage keys.
   * @example
   * clearOverviewSessionStorage()
   * undefined
   * @returns {void} No return value.
   */
  private clearOverviewSessionStorage(): void {
    const overviewKeys = [
      'sum_overdue_compliance',
      'overviewRemark_parent',
      'comeFromOverviewOverdueCompliance',
      'overview_filters'
    ];
 
    overviewKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove overview key: ${key}`, e);
      }
    });
  }
 
  /**
   * Removes all remarks related session storage keys.
   * @example
   * clearRemarksSessionStorage()
   * undefined
   * @returns {void} No return value.
   */
  private clearRemarksSessionStorage(): void {
    const remarksKeys = [
      'selected_compliance_type',
      'compliance_instance_id',
      'compliance_schedule_on_id',
      'overviewRemark_parent'
    ];
 
    remarksKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove remarks key: ${key}`, e);
      }
    });
  }
 
  /**
   * Removes all reopen compliance related session storage keys.
   * @example
   * clearReopenComplianceSessionStorage()
   * undefined
   * @returns {void} No return value.
   */
  private clearReopenComplianceSessionStorage(): void {
    const reopenKeys = [
      'for_month',
      'branch',
      'compliance_schedule_on_id',
      'customer_branch_id',
      'compliance_instance_id',
      'compliance_id',
      'rpt_type',
      'dueDate',
      'reopen_parent',
      'previousRoute'
    ];
 
    reopenKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove reopen compliance key: ${key}`, e);
      }
    });
  }
 
  /**
   * Dispatches Redux actions to clear component state for all components with saved filters.
   * @example
   * clearReduxComponentStates()
   * undefined
   * @returns {void} No return value.
   */
  private clearReduxComponentStates(): void {
    // Dispatch a global clear state action
    this.store.dispatch(clearAllState());
 
    // If you have component-specific clear actions, dispatch them here
    // Example:
    // this.store.dispatch(clearDetailedReportState());
    // this.store.dispatch(clearComplianceDocumentsState());
    // this.store.dispatch(clearOverviewState());
  }

}